export interface RegulationRule {
  id: string;
  title: string;
  value: string;
  source: string; // "CCQ 3.8.3.4" / "CSA B651:18 §5.5.8" / "GoRampe interne"
  category: 'slope' | 'dimension' | 'safety' | 'material' | 'business';
  description?: string;
  usage?: 'res' | 'com' | 'both';
}

export const REGULATIONS: RegulationRule[] = [
  {
    id: 'slope-1-12',
    title: 'Pente maximale conforme PMR',
    value: '1:12 (8.33%)',
    source: 'CCQ 3.8.3.4 + CSA B651:18',
    category: 'slope',
    description:
      'Pente maximale autorisée pour usage accessibilité (personnes à mobilité réduite).',
    usage: 'both',
  },
  {
    id: 'run-max',
    title: 'Volée maximale sans palier',
    value: '9 000 mm (354" / 30 pi)',
    source: 'CCQ',
    category: 'dimension',
    description: 'Au-delà, palier de repos obligatoire.',
    usage: 'both',
  },
  {
    id: 'landing-size',
    title: 'Palier de repos',
    value: '1 500 × 1 500 mm',
    source: 'CCQ + CSA B651',
    category: 'dimension',
    description: "Dimensions minimales d'un palier intermédiaire ou de virage.",
    usage: 'both',
  },
  {
    id: 'rail-required',
    title: 'Garde-corps obligatoire',
    value: 'Chute > 600 mm (24")',
    source: 'CCQ',
    category: 'safety',
    description: 'Garde-corps requis dès que la hauteur de chute dépasse 600 mm.',
    usage: 'both',
  },
  {
    id: 'rail-height-res',
    title: 'Hauteur garde-corps résidentiel',
    value: '36"',
    source: 'CCQ 9.8.8',
    category: 'safety',
    usage: 'res',
  },
  {
    id: 'rail-height-com',
    title: 'Hauteur garde-corps commercial',
    value: '42"',
    source: 'CCQ 3.4.6',
    category: 'safety',
    usage: 'com',
  },
  {
    id: 'handrails-both-sides',
    title: 'Mains courantes 2 côtés',
    value: 'Obligatoire',
    source: 'CSA B651:18 §5.5.8 + CCQ',
    category: 'safety',
    description: 'Mains courantes continues des deux côtés de la rampe.',
    usage: 'com',
  },
  {
    id: 'width-free',
    title: 'Largeur libre minimale',
    value: '870 mm (≈ 34.25")',
    source: 'CCQ 3.8.3.4',
    category: 'dimension',
    description: 'Largeur libre minimale entre les mains courantes.',
    usage: 'both',
  },
  {
    id: 'width-com-recommended',
    title: 'Largeur recommandée commerciale',
    value: '1 100 mm (≈ 44")',
    source: 'CSA B651:18 + CCQ 3.8',
    category: 'dimension',
    usage: 'com',
  },
  {
    id: 'clear-space',
    title: 'Espace de manœuvre haut/bas',
    value: '1 500 × 1 500 mm',
    source: 'CCQ 3.8.3.3',
    category: 'dimension',
    description: 'Zone libre requise au début et à la fin de la rampe pour manœuvres.',
    usage: 'both',
  },
];

export interface GoRampeRule {
  id: string;
  title: string;
  value: string;
  rationale: string;
  flag: 'ok' | 'warn' | 'limit' | 'max-absolute' | 'error';
}

export const GORAMPE_RULES: GoRampeRule[] = [
  {
    id: 'slope-1-12',
    title: 'Pente 1:12',
    value: '≤ 8.33%',
    rationale: 'Conforme CCQ + PMR. Choix idéal.',
    flag: 'ok',
  },
  {
    id: 'slope-1-11',
    title: 'Pente 1:11',
    value: '9.09%',
    rationale: "Léger dépassement légal — acceptable si contrainte d'espace.",
    flag: 'warn',
  },
  {
    id: 'slope-1-10',
    title: 'Pente 1:10',
    value: '10%',
    rationale: 'Au-delà CCQ — usage récréatif/transport non-PMR.',
    flag: 'warn',
  },
  {
    id: 'slope-1-9',
    title: 'Pente 1:9',
    value: '11.11%',
    rationale: "Limite GoRampe conseillée — risque d'effort important.",
    flag: 'limit',
  },
  {
    id: 'slope-1-8',
    title: 'Pente 1:8',
    value: '12.5%',
    rationale: 'Maximum absolu GoRampe — illégal pour PMR mais utilisable pour seuil temporaire.',
    flag: 'max-absolute',
  },
];

export interface WizardStep {
  id: string;
  number: number;
  title: string;
  description: string;
  triggers: string[]; // Quelles règles cette étape impacte
}

export const WIZARD_STEPS: WizardStep[] = [
  {
    id: 'measures',
    number: 1,
    title: 'Mesures',
    description:
      'Hauteur du seuil, longueur disponible, largeur souhaitée. Choix usage résidentiel ou commercial.',
    triggers: [
      'Hauteur > 600 mm → garde-corps obligatoire',
      'Usage commercial → plancher plein verrouillé',
      'Largeur 36" résidentiel / 48" commercial recommandé',
    ],
  },
  {
    id: 'shape',
    number: 2,
    title: 'Forme',
    description: 'Droite, L à 90°, U à 180°, switchback. Sélection des bras et direction.',
    triggers: [
      'Forme L/U → palier de virage 1500×1500mm requis',
      'Longueur > 9 m → palier de repos intermédiaire automatique',
    ],
  },
  {
    id: 'options',
    number: 3,
    title: 'Options',
    description:
      'Type de garde-corps (aucun, ajouré, barrotin, barrotin+mains). Plancher (plein, maille).',
    triggers: [
      'Commercial → barrotin_mains recommandé (mains courantes 2 côtés CSA B651)',
      'Commercial → plancher plein obligatoire',
    ],
  },
  {
    id: 'recap',
    number: 4,
    title: 'Récapitulatif',
    description: 'Visualisation 3D + BOM + total. Validation finale avec alertes CCQ/CSA.',
    triggers: ['Affiche errors (à corriger) et warnings (à surveiller)'],
  },
  {
    id: 'client',
    number: 5,
    title: 'Client',
    description: 'Coordonnées, livraison, location vs achat. Génération PDF + envoi.',
    triggers: [
      'Tarifs install/uninstall 36"/48"',
      'Transport 2.20$/km (sans uninstall) ou 4.40$/km (avec uninstall)',
    ],
  },
];

export interface RegulatoryPdf {
  id: string;
  title: string;
  filename: string;
  source: string; // CCQ, CSA, ADA, SHQ, GoRampe
  language: 'fr' | 'en' | 'both';
  description: string;
}

export const REGULATORY_PDFS: RegulatoryPdf[] = [
  {
    id: 'charte-fr',
    title: "Charte d'inclinaisons (FR)",
    filename: 'charte-inclinaisons-fr.pdf',
    source: 'GoRampe',
    language: 'fr',
    description: 'Tableau hauteur × longueur → angle. Officiel GoRampe.',
  },
  {
    id: 'charte-en',
    title: 'Inclinaisons chart (EN)',
    filename: 'charte-inclinaisons-en.pdf',
    source: 'GoRampe',
    language: 'en',
    description: 'Height × length → angle table. Official GoRampe.',
  },
  {
    id: 'ccq-rampes',
    title: "CCQ — Conception des rampes d'accès",
    filename: 'ccq-norme-rampes.pdf',
    source: 'CCQ',
    language: 'fr',
    description: 'Norme québécoise pour conception des rampes.',
  },
  {
    id: 'ccq-rails',
    title: 'CCQ — Mains courantes et garde-corps',
    filename: 'ccq-mains-courantes.pdf',
    source: 'CCQ',
    language: 'fr',
    description: 'Spécifications mains courantes et garde-corps.',
  },
  {
    id: 'csa-b651',
    title: 'CSA B651:2012',
    filename: 'csa-b651-2012.pdf',
    source: 'CSA',
    language: 'fr',
    description: "Norme canadienne d'accessibilité universelle.",
  },
  {
    id: 'shq-091',
    title: 'SHQ-091-10 — Programme PAD',
    filename: 'shq-091-10-pad.pdf',
    source: 'SHQ',
    language: 'fr',
    description: "Critères d'éligibilité au Programme d'Adaptation du Domicile.",
  },
  {
    id: 'ada',
    title: 'ADA 2010 — Standards',
    filename: 'ada-2010.pdf',
    source: 'ADA (USA)',
    language: 'en',
    description: 'Americans with Disabilities Act Standards 2010.',
  },
  {
    id: 'comparatif',
    title: 'Comparatif codes du bâtiment',
    filename: 'comparatif-codes.pdf',
    source: 'Multi (CCQ/CNB/ADA)',
    language: 'fr',
    description: 'Comparatif rampes entre CCQ, CNB, ADA.',
  },
  {
    id: 'garantie-fr',
    title: 'Garantie GoRampe (FR)',
    filename: 'garantie-fr.pdf',
    source: 'GoRampe',
    language: 'fr',
    description: 'Document de garantie produit officiel.',
  },
  {
    id: 'garantie-en',
    title: 'Warranty GoRampe (EN)',
    filename: 'garantie-en.pdf',
    source: 'GoRampe',
    language: 'en',
    description: 'Official product warranty document.',
  },
];
